import { Controller } from '@hotwired/stimulus'
import * as Sentry from '@sentry/browser'
import posthog from 'posthog-js'

import { PlaidError } from '../errors'

// Connects to data-controller="plaid-link"
export default class extends Controller {
  static targets = [
    'connectButton',
  ]

  declare readonly connectButtonTarget: HTMLButtonElement

  static values = {
    completedPath: String,
    csrfToken: String,
    errorCsrfToken: String,
    errorPath: String,
    linkContext: String,
    linkToken: String,
    purpose: String,
    tokenPath: String,
  }

  declare csrfTokenValue: string
  declare errorCsrfTokenValue: string
  declare tokenPathValue: string
  declare errorPathValue: string
  declare linkTokenValue: string
  declare linkContextValue: string
  declare completedPathValue: string
  declare purposeValue: string

  plaidHandler?: ReturnType<typeof window.Plaid['create']>

  initialize () {
    // Plaid's only NPM package is React specific, so we load the Plaid Link client
    // via `javascript_include_tag` (https://cdn.plaid.com/link/v2/stable/link-initialize.js)
    // which makes it available as `window.Plaid`.
    this.plaidHandler = window.Plaid.create({
      token: this.linkTokenValue,
      onSuccess: (publicToken, metadata) => {
        // TODO: add eslint-disable-next-line for bare fetch
        fetch(this.tokenPathValue, {
          method: 'POST',
          headers: {
            'X-CSRF-Token': this.csrfTokenValue,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(
            {
              plaid:
              {
                public_token: publicToken,
                accounts: metadata.accounts,
                institution: metadata.institution,
                link_session_id: metadata.link_session_id,
              },
            }
          ),
        }).then(response => {
          Sentry.addBreadcrumb({
            category: 'Plaid',
            message: 'fetch response',
            data: {
              status: response.status,
              request_id: response.headers.get('x-request-id') || '',
            },
          })
          if (!response.ok) {
            throw new PlaidError(response.statusText)
          }

          this.connectButtonTarget.disabled = true
          this.connectButtonTarget.classList.add('opacity-50')
          window.location.replace(this.completedPathValue)
        })
      },

      onExit: (err, metadata) => {
        if (err != null) {
          // TODO: add eslint-disable-next-line for bare fetch
          fetch(this.errorPathValue, {
            method: 'POST',
            headers: {
              'X-CSRF-Token': this.errorCsrfTokenValue,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              error: {
                error_type: err.error_type,
                error_code: err.error_code,
                link_context: this.linkContextValue,
                link_session_id: metadata.link_session_id,
              },
            }),
          }).then(response => {
            Sentry.addBreadcrumb({
              category: 'Plaid',
              message: 'link error response',
              data: {
                status: response.status,
                request_id: response.headers.get('x-request-id') || '',
              },
            })
            if (!response.ok) {
              throw new PlaidError(response.statusText)
            }

            this.connectButtonTarget.disabled = true
            this.connectButtonTarget.classList.add('opacity-50')
            window.location.replace(this.completedPathValue)
          })
        }
      },

      onEvent: (eventName, metadata) => {
        let sanitizedMetadata: { [key:string]: string | null } = {
          link_session_id: metadata.link_session_id,
          plaid_request_id: metadata.request_id,
          institution_id: metadata.institution_id || '',
        }

        switch (eventName) {
          case 'ERROR':
          case 'EXIT':
            sanitizedMetadata = {
              ...sanitizedMetadata,
              error_type: metadata.error_type || '',
              error_code: metadata.error_code || '',
              error_message: metadata.error_message || '',
            }
            break
          case 'TRANSITION_VIEW':
            sanitizedMetadata = {
              ...sanitizedMetadata,
              view_name: metadata.view_name,
            }
            break
        }

        const phEventName = eventName === 'TRANSITION_VIEW'
          ? `Plaid transition view ${metadata.view_name}`
          : `Plaid event ${eventName}`

        posthog.capture(phEventName, metadata, { timestamp: new Date(metadata.timestamp) })

        Sentry.addBreadcrumb({
          category: 'Plaid',
          message: eventName,
          data: sanitizedMetadata,
        })
      },
    })

    this.enterLinkIfRequested()
  }

  enterLinkIfRequested () {
    const url = new URL(window.location.href)
    if (this.purposeValue === url.searchParams.get('plaid_flow')) {
      this.element.scrollIntoView()

      url.searchParams.delete('plaid_flow')
      history.replaceState(null, '', url)

      this.enterPlaidLinkFlow()
    }
  }

  enterPlaidLinkFlow () {
    this.plaidHandler!.open()
  }
}
